/**
 * Client-side error reporting.
 *
 * Forwards uncaught SPA errors to the gateway's ops ingest (`/api/client-errors`)
 * so they reach error@i2tdigital.com — but ONLY in production (the backend gates
 * the email on ENVIRONMENT; here we always attempt, the server decides). Every
 * report is best-effort, deduped + throttled client-side, and NEVER throws or
 * blocks the UI. The matching gateway ingest endpoint is the backend half.
 */
import { getConfig } from '@/config';

const INGEST_PATH = '/api/client-errors';
const THROTTLE_MS = 60_000;
const MAX_KEYS = 200;
const _seen = new Map<string, number>();

function _throttled(signature: string): boolean {
  const now = Date.now();
  const last = _seen.get(signature);
  if (last !== undefined && now - last < THROTTLE_MS) return true;
  if (_seen.size > MAX_KEYS) _seen.clear();
  _seen.set(signature, now);
  return false;
}

export interface ClientErrorReport {
  message: string;
  stack?: string;
  /** where it came from: 'render' | 'route' | 'window.onerror' | 'unhandledrejection' */
  source?: string;
  componentStack?: string;
  url?: string;
}

export function reportClientError(report: ClientErrorReport): void {
  try {
    const message = (report.message || 'Unknown error').slice(0, 500);
    if (_throttled(`${report.source ?? ''}|${message}`)) return;

    const env = import.meta.env as Record<string, string | undefined>;
    const body = JSON.stringify({
      message,
      stack: (report.stack || '').slice(0, 4000),
      component_stack: (report.componentStack || '').slice(0, 4000),
      source: report.source ?? 'client',
      url: report.url ?? (typeof location !== 'undefined' ? location.href : ''),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      release: env.VITE_RELEASE ?? '',
    });

    let base = '';
    try {
      base = getConfig().apiBaseUrl;
    } catch {
      base = '';
    }
    // keepalive → the report still flushes if the page is reloading/navigating.
    void fetch(`${base}${INGEST_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* best-effort — never surface a reporting failure */
    });
  } catch {
    /* reporting must never throw */
  }
}

/** Attach window-level handlers for uncaught errors + unhandled promise rejections. */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e: ErrorEvent) => {
    const message = e.message || e.error?.message || '';
    if (!message) return; // ignore resource-load errors with no message (<img> etc.)
    reportClientError({
      message,
      stack: e.error?.stack,
      source: 'window.onerror',
      url: e.filename,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined;
    const message = reason?.message ?? String(reason ?? 'Unhandled rejection');
    reportClientError({ message, stack: reason?.stack, source: 'unhandledrejection' });
  });
}
